import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import { lettersOnlyWithSpaceAndHyphenRegex } from 'app/common/utils';
import { Success } from 'app/components/Notifications';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import useClickOutside from 'app/hooks/useClickOutside';
import clsx from 'clsx';
import { useNotificationsContext } from 'context';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { userInfoApi } from 'services/api/user-info/queries';
import z from 'zod';

interface Props {
  initialValue: string;
  onChange: (value: string) => void;
  close: () => void;
}

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'settings.dialogs.edit_last_name.field_is_required' })
    .max(64, { error: 'maximum_length_exceeded' })
    .regex(lettersOnlyWithSpaceAndHyphenRegex),
});

type FormData = z.infer<typeof schema>;

export default function EditLastNameDialog({
  initialValue,
  onChange,
  close,
}: Props) {
  const { t } = useTranslation();

  const [updateUser, { isLoading }] = userInfoApi.useUpdateUserInfoMutation();
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const { addNotification } = useNotificationsContext();

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(close);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: {
      name: initialValue,
    },
  });

  async function onSubmit({ name }: FormData) {
    try {
      await updateUser({
        lastName: name,
      });
      addNotification(
        <Success
          text={t('settings.dialogs.edit_last_name.success_name_update')}
        />,
      );
      onChange(name);
      close();
    } catch (error) {
      addErrorModal();
    }
  }

  return (
    <Dialog className="modal-form">
      <div className="modal-container relative">
        <span
          className="back-button absolute"
          onClick={() => {
            close();
          }}
        >
          <svg
            id="ic_nav_back"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <rect id="Box" width="48" height="48" fill="none" />
            <path
              id="ic_nav_up"
              d="M16.465,8.482H1.483"
              transform="translate(15.518 15.518)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
            <path
              id="Path_470"
              data-name="Path 470"
              d="M23.676,16.018,16.2,23.748l7.477,7.73"
              transform="translate(0.324 0.252)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>
        </span>
        <>
          <h2>{t('settings.dialogs.edit_last_name.title')}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={clsx('form-group', { error: !!errors.name })}>
              <div className="form-content">
                <div className="form-label">
                  <span>{t('settings.dialogs.edit_last_name.label')}*</span>
                </div>
                <div className="form-control">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        autoComplete="off"
                        placeholder={t(
                          'settings.dialogs.edit_last_name.placeholder',
                        )}
                        disabled={isLoading}
                        onChange={e => {
                          const nextValue = e.value || '';

                          const cleanValue = nextValue
                            .split('')
                            .filter(char =>
                              lettersOnlyWithSpaceAndHyphenRegex.test(char),
                            )
                            .join('');

                          field.onChange(cleanValue);
                        }}
                      />
                    )}
                  />
                </div>
              </div>

              {errors.name?.message && (
                <div className="form-error">
                  <Error>
                    {t<string>(errors.name.message, { length: 64 })}
                  </Error>
                </div>
              )}
            </div>

            <Button themeColor="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <div className="loading_icon loading_icon-btn">
                  <div className="css-icon"></div>
                </div>
              ) : (
                t('save')
              )}
            </Button>
          </form>
        </>
      </div>
    </Dialog>
  );
}
